<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="800px"
    >
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="branch.id" class="text-h5">Update branch</h2>
              <h2 v-else class="text-h5">Add a branch</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close ml-md-6"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                v-if="isAdmin"
                id="branch_code"
                v-slot="{ errors }"
                name="branch_code"
                rules="required"
              >
                <v-text-field
                  v-model="branch.code"
                  label="Branch code"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="isAdmin"
                id="description"
                v-slot="{ errors }"
                name="description"
                rules="required"
              >
                <v-text-field
                  v-model="branch.description"
                  label="Branch description"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                rounded
                large
                depressed
                :loading="buttonLoading"
                color="primary"
                class="ml-4 mb-1"
                @click="save()"
              >
                <span>Save</span>
                <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import {
    CREATE_OR_UPDATE_BRANCH_MUTATION,
    COMPANY_BRANCHES_QUERY,
  } from '../../../constants/graphql/branches';
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';
  import { formHelperMixin } from '../../../mixins/form-helper-mixin';

  export default {
    name: 'BranchDialog',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      branchToUpdate: {
        type: Object,
        default: () => {
          return {};
        },
      },
    },
    data() {
      return {
        branch: this.branchDefaults(),
        errorMessage: null,
        showSuccess: false,
        successMessage: null,
      };
    },

    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isEdit() {
        return this.branch.id;
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          if (this.branchToUpdate.id) {
            this.branch = JSON.parse(JSON.stringify(this.branchToUpdate));
          }
        }
      },
    },
    methods: {
      branchDefaults() {
        return {
          id: undefined,
          code: '',
          description: '',
          companyId: undefined,
        };
      },
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.createOrUpdateBranch();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createOrUpdateBranch() {
        const branchInputDetails = this.branchInputDetails();
        const id = this.branch.id;
        this.$apollo
          .mutate({
            mutation: CREATE_OR_UPDATE_BRANCH_MUTATION,
            variables: {
              id,
              branchInputDetails,
            },
            update: (cache, { data: { createOrUpdateBranch } }) => {
              this.updateCacheAfterCreateOrUpdateBranch(
                cache,
                createOrUpdateBranch
              );
            },
          })
          .then(_response => {
            this.successMessage = this.isEdit
              ? 'Branch successfully updated'
              : 'Branch successfully created';
            this.showSuccess = true;
            this.closeDialog();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.displayErrorAndCloseDialog();
            } else {
              this.displayErrorAndCloseDialog();
              throw error;
            }
          });
      },
      updateCacheAfterCreateOrUpdateBranch(cache, createOrUpdateBranch) {
        try {
          const query = {
            query: COMPANY_BRANCHES_QUERY,
            variables: {
              id: this.$route.params.id,
            },
          };
          const data = cache.readQuery(query);
          if (this.isEdit) {
            //Create a reference type and update it
            var branch = data.companyBranches.find(
              branch => branch.id == createOrUpdateBranch.id
            );
            var branch = createOrUpdateBranch;
          } else {
            data.companyBranches.push(createOrUpdateBranch);
          }
          cache.writeQuery({ ...query, data: data });
        } catch (err) {
          if (err.name == 'Invariant Violation') {
            console.log('COMPANY_BRANCHES_QUERY not in cache store yet.');
          } else {
            throw err;
          }
        }
      },
      branchInputDetails() {
        return {
          code: this.branch.code,
          description: this.branch.description,
          companyId: this.companyId(),
        };
      },
      companyId() {
        if (this.isEdit) {
          return this.branch.companyId;
        } else {
          return this.branchToUpdate.companyId;
        }
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.branch = this.branchDefaults();
        this.uploadedImage = null;
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .modal-close {
    float: right;
  }

  .default-executor-fee-txt-field {
    max-width: 200px;
  }

  .file-upload-btn {
    display: inline-flex;
  }

  .current-company-logo-link {
    color: grey;
    margin-left: 40px;
  }
</style>
